import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { wattelEMApi, DeviceOverview } from '../api/wattelEM';
import { architectureApi, InstallationData } from '../api/architecture';
import { displaySn } from '../utils/deviceDisplay';
import { generateRapportRSE } from '../utils/generateRapportRSE';
import { useI18n } from '../i18n';

// ─── Types ──────────────────────────────────────────────────────────

type TabKey = 'identification' | 'site' | 'consommations' | 'objectifs' | 'carbone' | 'actions' | 'synthese';

interface ActionItem {
  action: string;
  statut: 'realise' | 'en-cours' | 'planifie' | 'planifie-mt';
  gain: number;
  co2: number;
  responsable: string;
  priorite: 'HAUTE' | 'MOYENNE' | 'FAIBLE';
}

// ─── Helpers ────────────────────────────────────────────────────────

function fmtNum(n: number | null | undefined, locale: string): string {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return Math.round(n).toLocaleString(locale);
}

function pct(v: number): string {
  return (v >= 0 ? '+' : '') + v.toFixed(1) + ' %';
}

type TFn = ReturnType<typeof useI18n>['t'];

const statusLabels = (t: TFn): Record<string, string> => ({
  realise: t('adm_rse_status_done'),
  'en-cours': t('adm_rse_status_in_progress'),
  planifie: t('adm_rse_status_planned'),
  'planifie-mt': t('adm_rse_status_planned_mt'),
});

const priorityLabels = (t: TFn): Record<ActionItem['priorite'], string> => ({
  HAUTE: t('adm_rse_priority_high'),
  MOYENNE: t('adm_rse_priority_medium'),
  FAIBLE: t('adm_rse_priority_low'),
});

const getTabs = (t: TFn): { key: TabKey; label: string; num: string }[] => [
  { key: 'identification', label: t('adm_rse_tab_identification'), num: '1' },
  { key: 'site', label: t('adm_rse_tab_site'), num: '2' },
  { key: 'consommations', label: t('adm_rse_tab_consumption'), num: '3' },
  { key: 'objectifs', label: t('adm_rse_tab_targets'), num: '4' },
  { key: 'carbone', label: t('adm_rse_tab_carbon'), num: '5' },
  { key: 'actions', label: t('adm_rse_tab_actions'), num: '6' },
  { key: 'synthese', label: t('adm_rse_tab_summary'), num: '7' },
];

// ─── Page ───────────────────────────────────────────────────────────

export default function RapportRSEPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const fmt = (n: number | null | undefined) => fmtNum(n, locale);
  const STATUS_LABELS = statusLabels(t);
  const PRIORITY_LABELS = priorityLabels(t);
  const TABS = getTabs(t);
  const installationIdParam = searchParams.get('installationId');

  const [activeTab, setActiveTab] = useState<TabKey>('identification');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // ── Data from API (auto-filled) ──
  const [devices, setDevices] = useState<DeviceOverview[]>([]);
  const [installations, setInstallations] = useState<InstallationData[]>([]);
  const [dailyEnergy, setDailyEnergy] = useState<{ day: string; daily_kwh: number }[]>([]);

  // ── Editable fields ──
  // Identification
  const [entreprise, setEntreprise] = useState('');
  const [batiment, setBatiment] = useState('');
  const [adresse, setAdresse] = useState('');
  const [responsable, setResponsable] = useState('');
  const [dateRapport, setDateRapport] = useState(new Date().toISOString().slice(0, 10));

  // Période
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 12);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Site
  const [surface, setSurface] = useState(0);
  const [activite, setActivite] = useState(() => t('adm_rse_default_activity'));
  const [anneeRef, setAnneeRef] = useState('2020');
  const [consoRef, setConsoRef] = useState(0);

  // Carbone
  const [facteurElec, setFacteurElec] = useState(0.0569);
  const [co2Ref, setCo2Ref] = useState(0);

  // Actions
  const [actions, setActions] = useState<ActionItem[]>(() => [
    { action: t('adm_rse_default_action'), statut: 'en-cours', gain: 0, co2: 0, responsable: '—', priorite: 'HAUTE' },
  ]);

  // Re-translate the pre-filled defaults on a live language switch, only while untouched
  const defAct = t('adm_rse_default_activity');
  const defAction = t('adm_rse_default_action');
  const prevDefs = useRef({ act: defAct, action: defAction });
  useEffect(() => {
    const p = prevDefs.current;
    if (p.act === defAct && p.action === defAction) return;
    setActivite(v => (v === p.act ? defAct : v));
    setActions(list => list.map(a => (a.action === p.action ? { ...a, action: defAction } : a)));
    prevDefs.current = { act: defAct, action: defAction };
  }, [defAct, defAction]);

  // ── Fetch data ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [devRes, instRes] = await Promise.allSettled([
        wattelEMApi.getOverview(),
        architectureApi.getInstallations().then(r => r.data),
      ]);
      const devs = devRes.status === 'fulfilled' ? devRes.value : [];
      const insts = instRes.status === 'fulfilled' ? instRes.value : [];
      setDevices(devs);
      setInstallations(insts);

      // Pre-fill identification from installation
      const targetInst = installationIdParam
        ? insts.find(i => i.id === Number(installationIdParam))
        : insts[0];
      if (targetInst) {
        setEntreprise(targetInst.name);
        setBatiment(targetInst.name);
      }

      // Get device SNs: use overview devices (they know their installation via zones)
      // If installationId is specified, try filtering. If no devices found, use all.
      let relevantSns: string[];
      if (installationIdParam) {
        const filtered = devs.filter(d => d.zones.some(z => z.installation_id === Number(installationIdParam)));
        relevantSns = filtered.length > 0 ? filtered.map(d => d.device_sn) : devs.map(d => d.device_sn);
      } else {
        relevantSns = devs.map(d => d.device_sn);
      }
      const uniqueSns = [...new Set(relevantSns)];

      console.log('[RapportRSE] Devices from overview:', devs.length, '| With zones:', devs.filter(d => d.zones.length > 0).length);
      console.log('[RapportRSE] Fetching energy for', uniqueSns.length, 'devices:', uniqueSns, 'period:', startDate, '→', endDate);

      const energyResults = await Promise.allSettled(
        uniqueSns.map(sn => wattelEMApi.getDailyEnergy(sn, 365, startDate, endDate))
      );
      
      // Log failures for debugging
      energyResults.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.warn(`[RapportRSE] getDailyEnergy failed for ${uniqueSns[i]}:`, r.reason);
        }
      });

      const allEnergy = energyResults
        .filter((r): r is PromiseFulfilledResult<any[]> => r.status === 'fulfilled')
        .flatMap(r => r.value);
      console.log('[RapportRSE] Energy results:', allEnergy.length, 'data points, total kWh:', allEnergy.reduce((s, d) => s + (parseFloat(d.daily_kwh) || 0), 0));
      setDailyEnergy(allEnergy);
    } catch (err) {
      console.error('[RapportRSE] Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [installationIdParam, startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Derived values ──
  const targetInsts = installationIdParam
    ? installations.filter(i => i.id === Number(installationIdParam))
    : installations;
  const totalEnergy = dailyEnergy.reduce((s, d) => s + (Number(d.daily_kwh) || 0), 0);
  const totalZones = targetInsts.reduce((s, i) => s + i.zones.filter(z => !z.name.startsWith('Tableau')).length, 0);
  const totalEquipment = targetInsts.reduce((s, i) => s + i.zones.reduce((zs, z) => zs + (z.equipment?.length || 0), 0), 0);
  const installedDevices = (() => {
    if (!installationIdParam) return devices;
    const filtered = devices.filter(d => d.zones.some(z => z.installation_id === Number(installationIdParam)));
    return filtered.length > 0 ? filtered : devices;
  })();
  const onlineDevices = installedDevices.filter(d => d.online);
  const emissionsCO2_t = (totalEnergy * facteurElec) / 1000;
  const reductionPct = consoRef > 0 ? ((consoRef - totalEnergy) / consoRef) * 100 : 0;

  // Energy by month
  const energyByMonth: Array<{ month: string; year: number; totalEnergy: number }> = [];
  const monthMap = new Map<string, number>();
  dailyEnergy.forEach(d => {
    const date = new Date(d.day);
    const key = `${date.getMonth() + 1}/${date.getFullYear()}`;
    monthMap.set(key, (monthMap.get(key) || 0) + (Number(d.daily_kwh) || 0));
  });
  monthMap.forEach((energy, key) => {
    const [month, year] = key.split('/');
    energyByMonth.push({ month, year: Number(year), totalEnergy: energy });
  });

  // Top devices
  const topDevices = installedDevices
    .filter(d => d.current?.energy_kwh)
    .sort((a, b) => Number(b.current?.energy_kwh || 0) - Number(a.current?.energy_kwh || 0))
    .slice(0, 10)
    .map(d => ({ deviceName: displaySn(d.device_sn), totalEnergy: Number(d.current?.energy_kwh || 0) }));

  // ── Export PDF ──
  const handleExport = async () => {
    setGenerating(true);
    try {
      const doc = await generateRapportRSE({
        organisationName: entreprise || t('adm_rse_organisation'),
        periode: { startDate, endDate },
        totalEnergyKwh: totalEnergy,
        totalCost: totalEnergy * 0.18,
        totalDevices: installedDevices.length,
        activeDevices: onlineDevices.length,
        totalTransactions: 0,
        energyByMonth,
        topDevices,
        revenueByDevice: [],
        energyByVehicle: [],
        totalInstallations: targetInsts.length,
        totalZones,
        totalEquipment,
      });
      doc.save(`${t('adm_rse_file_prefix')}_${entreprise.replace(/\s+/g, '_') || 'export'}_${startDate}_${endDate}.pdf`);
    } catch (err) {
      console.error('[RapportRSE] Export error:', err);
      alert(t('adm_rse_pdf_error', { msg: (err as Error).message }));
    } finally {
      setGenerating(false);
    }
  };

  // ── Actions CRUD ──
  const addAction = () => setActions([...actions, { action: t('adm_rse_new_action'), statut: 'planifie', gain: 0, co2: 0, responsable: '—', priorite: 'MOYENNE' }]);
  const removeAction = (i: number) => setActions(actions.filter((_, idx) => idx !== i));
  const updateAction = (i: number, field: keyof ActionItem, value: any) => {
    const copy = [...actions];
    (copy[i] as any)[field] = value;
    setActions(copy);
  };

  // ── Render ──
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0b1e3f] to-[#1a6b3c] rounded-2xl p-5 shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="text-white/60 hover:text-white transition-colors" title={t('adm_rse_back')} aria-label={t('adm_rse_back')}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">{t('adm_rse_title')}</h1>
              <p className="text-emerald-200/80 text-xs mt-0.5">{t('adm_rse_subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="text-xs bg-white/10 border border-white/20 rounded-lg px-2.5 py-2 text-white focus:outline-none focus:ring-1 focus:ring-emerald-400/50 [color-scheme:dark]"
            />
            <span className="text-white/60 text-xs">→</span>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="text-xs bg-white/10 border border-white/20 rounded-lg px-2.5 py-2 text-white focus:outline-none focus:ring-1 focus:ring-emerald-400/50 [color-scheme:dark]"
            />
            <button
              onClick={handleExport}
              disabled={generating || loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 transition-all shadow-md"
            >
              {generating ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              )}
              {t('adm_rse_export_pdf')}
            </button>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-3" />
          <p className="text-gray-500 text-sm">{t('adm_rse_loading')}</p>
        </div>
      )}

      {!loading && (
        <>
          {/* Tabs */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-all ${
                    activeTab === tab.key
                      ? 'border-emerald-500 text-emerald-700 bg-emerald-50/50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="font-bold mr-1">{tab.num}.</span>{tab.label}
                </button>
              ))}
            </div>

            <div className="p-5">
              {/* TAB 1: Identification */}
              {activeTab === 'identification' && (
                <div className="space-y-4">
                  <h2 className="text-sm font-semibold text-emerald-700 uppercase tracking-wider border-b-2 border-emerald-500 pb-2">{t('adm_rse_h_identification')}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label={t('adm_rse_f_company')} value={entreprise} onChange={setEntreprise} />
                    <Field label={t('adm_rse_f_building')} value={batiment} onChange={setBatiment} />
                    <Field label={t('adm_rse_f_address')} value={adresse} onChange={setAdresse} />
                    <Field label={t('adm_rse_f_csr_manager')} value={responsable} onChange={setResponsable} />
                    <Field label={t('adm_rse_f_report_date')} value={dateRapport} onChange={setDateRapport} type="date" />
                  </div>
                </div>
              )}

              {/* TAB 2: Site & Données */}
              {activeTab === 'site' && (
                <div className="space-y-4">
                  <h2 className="text-sm font-semibold text-emerald-700 uppercase tracking-wider border-b-2 border-emerald-500 pb-2">{t('adm_rse_h_site')}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Field label={t('adm_rse_f_surface')} value={String(surface)} onChange={v => setSurface(Number(v) || 0)} type="number" />
                    <Field label={t('adm_rse_f_activity')} value={activite} onChange={setActivite} />
                    <Field label={t('adm_rse_f_ref_year')} value={anneeRef} onChange={setAnneeRef} />
                    <Field label={t('adm_rse_f_ref_conso')} value={String(consoRef)} onChange={v => setConsoRef(Number(v) || 0)} type="number" />
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                    <p className="text-xs text-blue-700 font-medium mb-2">{t('adm_rse_auto_data')}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <div><span className="text-gray-500">{t('adm_rse_auto_installations')}</span> <span className="font-semibold">{targetInsts.length}</span></div>
                      <div><span className="text-gray-500">{t('adm_rse_auto_zones')}</span> <span className="font-semibold">{totalZones}</span></div>
                      <div><span className="text-gray-500">{t('adm_rse_auto_equipment')}</span> <span className="font-semibold">{totalEquipment}</span></div>
                      <div><span className="text-gray-500">{t('adm_rse_auto_sensors')}</span> <span className="font-semibold">{installedDevices.length}</span></div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 3: Consommations */}
              {activeTab === 'consommations' && (
                <div className="space-y-4">
                  <h2 className="text-sm font-semibold text-emerald-700 uppercase tracking-wider border-b-2 border-emerald-500 pb-2">{t('adm_rse_h_consumption')}</h2>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <KpiCard label={t('adm_rse_kpi_total_energy')} value={`${fmt(totalEnergy)} kWh`} color="green" />
                    <KpiCard label={t('adm_rse_kpi_online_sensors')} value={`${onlineDevices.length} / ${installedDevices.length}`} color="blue" />
                    <KpiCard label={t('adm_rse_kpi_est_cost')} value={`${fmt(totalEnergy * 0.18)} €`} color="orange" />
                    <KpiCard label="kWh/m²" value={surface > 0 ? (totalEnergy / surface).toFixed(1) : '—'} color="purple" />
                  </div>

                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mt-4 pl-2 border-l-3 border-emerald-500">{t('adm_rse_monthly_trend')}</h3>
                  {energyByMonth.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-emerald-600 text-white">
                            <th className="px-3 py-2 text-left">{t('adm_rse_th_month')}</th>
                            <th className="px-3 py-2 text-right">kWh</th>
                            <th className="px-3 py-2 text-right">{t('adm_rse_th_mom')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {energyByMonth.map((m, i) => {
                            const prev = i > 0 ? energyByMonth[i - 1].totalEnergy : null;
                            const evol = prev ? ((m.totalEnergy - prev) / (prev || 1)) * 100 : null;
                            return (
                              <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                                <td className="px-3 py-2">{m.month}/{m.year}</td>
                                <td className="px-3 py-2 text-right font-medium">{fmt(m.totalEnergy)}</td>
                                <td className={`px-3 py-2 text-right ${evol !== null && evol < 0 ? 'text-green-600' : 'text-red-500'}`}>
                                  {evol !== null ? pct(evol) : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-400 text-xs italic">{t('adm_rse_no_monthly')}</p>
                  )}

                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mt-6 pl-2 border-l-3 border-emerald-500">{t('adm_rse_top_zones')}</h3>
                  {topDevices.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-blue-700 text-white">
                            <th className="px-3 py-2 text-left">{t('adm_rse_th_zone_device')}</th>
                            <th className="px-3 py-2 text-right">kWh</th>
                            <th className="px-3 py-2 text-right">{t('adm_rse_th_share')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topDevices.map((d, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                              <td className="px-3 py-2">{d.deviceName}</td>
                              <td className="px-3 py-2 text-right font-medium">{fmt(d.totalEnergy)}</td>
                              <td className="px-3 py-2 text-right">{totalEnergy > 0 ? ((d.totalEnergy / totalEnergy) * 100).toFixed(1) + ' %' : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-400 text-xs italic">{t('adm_rse_no_devices')}</p>
                  )}
                </div>
              )}

              {/* TAB 4: Objectifs */}
              {activeTab === 'objectifs' && (
                <div className="space-y-4">
                  <h2 className="text-sm font-semibold text-emerald-700 uppercase tracking-wider border-b-2 border-emerald-500 pb-2">{t('adm_rse_h_targets')}</h2>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <KpiCard label={t('adm_rse_kpi_reduction_achieved')} value={consoRef > 0 ? `${reductionPct.toFixed(1)} %` : '—'} color={reductionPct >= 40 ? 'green' : reductionPct >= 20 ? 'orange' : 'red'} />
                    <KpiCard label={t('adm_rse_kpi_current_conso')} value={`${fmt(totalEnergy)} kWh`} color="blue" />
                    <KpiCard label={t('adm_rse_kpi_kwh_m2_year')} value={surface > 0 ? (totalEnergy / surface).toFixed(1) : '—'} color="purple" />
                    <KpiCard label={t('adm_rse_kpi_target_2030')} value={consoRef > 0 ? `${fmt(consoRef * 0.6)} kWh` : '—'} color="orange" />
                  </div>

                  {consoRef > 0 && (
                    <div className="space-y-3">
                      {[
                        { label: '2030', target: 40 },
                        { label: '2040', target: 50 },
                        { label: '2050', target: 60 },
                      ].map(obj => {
                        const progress = Math.min((reductionPct / obj.target) * 100, 100);
                        const color = progress >= 100 ? 'bg-emerald-500' : progress >= 60 ? 'bg-orange-400' : 'bg-red-400';
                        return (
                          <div key={obj.label}>
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                              <span>{t('adm_rse_target_line', { year: obj.label, pct: obj.target })}</span>
                              <span>{reductionPct.toFixed(1)} % / {obj.target} %</span>
                            </div>
                            <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${progress}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {consoRef === 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-xs text-amber-700">
                      <p className="font-medium">{t('adm_rse_ref_missing')}</p>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 5: Carbone */}
              {activeTab === 'carbone' && (
                <div className="space-y-4">
                  <h2 className="text-sm font-semibold text-emerald-700 uppercase tracking-wider border-b-2 border-emerald-500 pb-2">{t('adm_rse_h_carbon')}</h2>
                  <p className="text-xs text-gray-500 mb-3">{t('adm_rse_factors_note')}</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <Field label={t('adm_rse_f_elec_factor')} value={String(facteurElec)} onChange={v => setFacteurElec(Number(v) || 0.0569)} type="number" step="0.001" />
                    <Field label={t('adm_rse_f_ref_emissions')} value={String(co2Ref)} onChange={v => setCo2Ref(Number(v) || 0)} type="number" step="0.1" />
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-emerald-600 text-white">
                          <th className="px-3 py-2 text-left">{t('adm_rse_th_source')}</th>
                          <th className="px-3 py-2 text-right">kWh</th>
                          <th className="px-3 py-2 text-right">{t('adm_rse_th_factor')}</th>
                          <th className="px-3 py-2 text-right">tCO₂e</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="bg-gray-50">
                          <td className="px-3 py-2">{t('adm_rse_elec_sensors')}</td>
                          <td className="px-3 py-2 text-right font-medium">{fmt(totalEnergy)}</td>
                          <td className="px-3 py-2 text-right">{facteurElec}</td>
                          <td className="px-3 py-2 text-right font-medium">{emissionsCO2_t.toFixed(3)}</td>
                        </tr>
                        <tr className="font-semibold border-t border-gray-300">
                          <td className="px-3 py-2">{t('adm_rse_total')}</td>
                          <td className="px-3 py-2 text-right">{fmt(totalEnergy)}</td>
                          <td className="px-3 py-2 text-right">—</td>
                          <td className="px-3 py-2 text-right">{emissionsCO2_t.toFixed(3)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                    <KpiCard label={t('adm_rse_kpi_total_emissions')} value={`${emissionsCO2_t.toFixed(2)} tCO₂e`} color="orange" />
                    <KpiCard label={t('adm_rse_kpi_avoided')} value={co2Ref > 0 ? `${(co2Ref - emissionsCO2_t).toFixed(2)} tCO₂e` : '—'} color="green" />
                    <KpiCard label={t('adm_rse_kpi_carbon_intensity')} value={totalEnergy > 0 ? `${(emissionsCO2_t * 1000 / totalEnergy).toFixed(4)} kgCO₂/kWh` : '—'} color="blue" />
                  </div>
                </div>
              )}

              {/* TAB 6: Actions */}
              {activeTab === 'actions' && (
                <div className="space-y-4">
                  <h2 className="text-sm font-semibold text-emerald-700 uppercase tracking-wider border-b-2 border-emerald-500 pb-2">{t('adm_rse_h_actions')}</h2>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-emerald-600 text-white">
                          <th className="px-2 py-2 text-left">{t('adm_rse_th_action')}</th>
                          <th className="px-2 py-2 text-left">{t('adm_rse_th_status')}</th>
                          <th className="px-2 py-2 text-right">{t('adm_rse_th_gain')}</th>
                          <th className="px-2 py-2 text-right">tCO₂e</th>
                          <th className="px-2 py-2 text-left">{t('adm_rse_th_owner')}</th>
                          <th className="px-2 py-2 text-left">{t('adm_rse_th_priority')}</th>
                          <th className="px-2 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {actions.map((a, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                            <td className="px-2 py-1.5">
                              <input value={a.action} onChange={e => updateAction(i, 'action', e.target.value)}
                                className="w-full min-w-[120px] text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-400" />
                            </td>
                            <td className="px-2 py-1.5">
                              <select value={a.statut} onChange={e => updateAction(i, 'statut', e.target.value)}
                                className="text-xs border border-gray-200 rounded px-1 py-1 focus:outline-none">
                                {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                              </select>
                            </td>
                            <td className="px-2 py-1.5">
                              <input type="number" value={a.gain} onChange={e => updateAction(i, 'gain', Number(e.target.value))}
                                className="w-16 text-xs text-right border border-gray-200 rounded px-1 py-1 focus:outline-none" />
                            </td>
                            <td className="px-2 py-1.5">
                              <input type="number" step="0.1" value={a.co2} onChange={e => updateAction(i, 'co2', Number(e.target.value))}
                                className="w-14 text-xs text-right border border-gray-200 rounded px-1 py-1 focus:outline-none" />
                            </td>
                            <td className="px-2 py-1.5">
                              <input value={a.responsable} onChange={e => updateAction(i, 'responsable', e.target.value)}
                                className="w-24 text-xs border border-gray-200 rounded px-1 py-1 focus:outline-none" />
                            </td>
                            <td className="px-2 py-1.5">
                              <select value={a.priorite} onChange={e => updateAction(i, 'priorite', e.target.value as any)}
                                className="text-xs border border-gray-200 rounded px-1 py-1 focus:outline-none">
                                {Object.entries(PRIORITY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                              </select>
                            </td>
                            <td className="px-2 py-1.5">
                              <button onClick={() => removeAction(i)} className="text-red-400 hover:text-red-600 text-sm" title={t('adm_rse_remove_action')} aria-label={t('adm_rse_remove_action')}>✕</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button onClick={addAction} className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                    {t('adm_rse_add_action')}
                  </button>
                </div>
              )}

              {/* TAB 7: Synthèse */}
              {activeTab === 'synthese' && (
                <div className="space-y-4">
                  <h2 className="text-sm font-semibold text-emerald-700 uppercase tracking-wider border-b-2 border-emerald-500 pb-2">{t('adm_rse_h_summary')}</h2>
                  
                  {/* Status badge */}
                  <div className={`rounded-xl p-4 border ${
                    reductionPct >= 40 ? 'bg-emerald-50 border-emerald-300' :
                    reductionPct >= 20 ? 'bg-amber-50 border-amber-300' : 'bg-red-50 border-red-300'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold">{entreprise || t('adm_rse_organisation')} — {batiment || t('adm_rse_site')}</p>
                        <p className="text-xs text-gray-600 mt-0.5">{t('adm_rse_period', { start: startDate, end: endDate })}</p>
                      </div>
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                        reductionPct >= 40 ? 'bg-emerald-200 text-emerald-800' :
                        reductionPct >= 20 ? 'bg-amber-200 text-amber-800' : 'bg-red-200 text-red-800'
                      }`}>
                        {reductionPct >= 40 ? t('adm_rse_badge_compliant') : reductionPct >= 20 ? t('adm_rse_badge_in_progress') : consoRef > 0 ? t('adm_rse_badge_not_met') : t('adm_rse_badge_not_assessed')}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <KpiCard label={t('adm_rse_kpi_total_energy')} value={`${fmt(totalEnergy)} kWh`} color="green" />
                    <KpiCard label={t('adm_rse_kpi_co2')} value={`${emissionsCO2_t.toFixed(2)} tCO₂e`} color="orange" />
                    <KpiCard label={t('adm_rse_kpi_reduction')} value={consoRef > 0 ? `${reductionPct.toFixed(1)} %` : '—'} color={reductionPct >= 40 ? 'green' : 'red'} />
                    <KpiCard label={t('adm_rse_kpi_active_sensors')} value={`${onlineDevices.length} / ${installedDevices.length}`} color="blue" />
                  </div>

                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <h3 className="text-xs font-semibold text-gray-600 uppercase mb-2">{t('adm_rse_overview')}</h3>
                    <ul className="space-y-1 text-xs text-gray-700">
                      <li>• <strong>{installedDevices.length}</strong> {t('adm_rse_sum_sensors', { online: onlineDevices.length })}</li>
                      <li>• <strong>{fmt(totalEnergy)}</strong> {t('adm_rse_sum_kwh')}</li>
                      <li>• <strong>{emissionsCO2_t.toFixed(2)}</strong> {t('adm_rse_sum_emissions')}</li>
                      <li>• <strong>{targetInsts.length}</strong> {t('adm_rse_sum_installations')}, <strong>{totalZones}</strong> {t('adm_rse_sum_zones')}, <strong>{totalEquipment}</strong> {t('adm_rse_sum_equipment')}</li>
                      <li>• {t('adm_rse_sum_cost')} <strong>{fmt(totalEnergy * 0.18)} €</strong></li>
                      {actions.length > 0 && <li>• <strong>{actions.length}</strong> {t('adm_rse_sum_actions', { gain: fmt(actions.reduce((s, a) => s + a.gain, 0)) })}</li>}
                    </ul>
                  </div>

                  <button
                    onClick={handleExport}
                    disabled={generating}
                    className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-xl bg-gradient-to-r from-emerald-500 to-blue-600 text-white hover:from-emerald-600 hover:to-blue-700 disabled:opacity-50 transition-all shadow-lg"
                  >
                    {generating ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    )}
                    {t('adm_rse_export_full')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────

function Field({ label, value, onChange, type = 'text', step }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; step?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">{label}</label>
      <input
        type={type}
        step={step}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:border-emerald-400 transition-all"
      />
    </div>
  );
}

function KpiCard({ label, value, color }: { label: string; value: string; color: string }) {
  const colorClasses: Record<string, string> = {
    green: 'text-emerald-600',
    blue: 'text-blue-600',
    orange: 'text-orange-500',
    red: 'text-red-500',
    purple: 'text-purple-600',
  };
  return (
    <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-lg font-bold ${colorClasses[color] || 'text-gray-900'}`}>{value}</p>
    </div>
  );
}
